import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Icon,
  InputCheckbox,
  StackedIcons,
  StyledRadios,
  Modal
} from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { DirectDebitPayment, LegacyDirectDebitFrame } from '@PetSureAustralia/petsure-payment/react-wrapper';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';
import { FullPetDetailsPropTypes } from '../../../propTypes/petDetails';
import { useReimbursementPopup } from './useReimbursementPopup'; // Import the custom hook
import './reimbursementPopup.scss';
import { PETSURE_UNDERWRITER, HOLLARD_UNDERWRITER, PAYMENT_METHOD_OPTION } from '../../../constants/constants';
// eslint-disable-next-line import/no-unresolved
import '@PetSureAustralia/petsure-payment/react-wrapper/index.esm.css';

export const ReimbursementPopup = ({ onCancel, activePet, setShowSuccessMessage }) => {
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isLegacyPaymentEnabled = cspConfiguration?.IsLegacyPaymentEnabled || false;
  const {
    fieldFormContext,
    showSelectAllCheckbox,
    policyDetails,
    showFailedUpdateError,
    petDetails,
    paymentMethod,
    supplierBusinessCode,
    ddRef,
    handleUpdateDirectDebit,
    handleClose,
    handlePaymentMethodChange,
    handlePaymentSuccess,
    isPartnerUsingLegacyPayment
  } = useReimbursementPopup({ activePet, onCancel, setShowSuccessMessage, isLegacyPaymentEnabled });

  const renderRadioButtons =
    !isLegacyPaymentEnabled || (isLegacyPaymentEnabled && activePet?.Underwriter === PETSURE_UNDERWRITER);

  const baseRadio = {
    label: 'Bank deposit',
    value: PAYMENT_METHOD_OPTION.CARD,
    labelCssClass: {
      checked: 'button primary',
      unchecked: 'button primary outline'
    },
    icon: {
      checked: (
        <StackedIcons
          customCssClass="offsetTick"
          icons={[
            <Icon
              key="icon1"
              icon="radio-unchecked"
            />,
            <Icon
              key="icon2"
              icon="tick"
              customCssClass="reveal animate-slow animate-once"
            />
          ]}
        />
      ),
      unchecked: <Icon icon="radio-unchecked" />
    }
  };

  const chequeRadio = {
    ...baseRadio,
    label: 'Cheque',
    value: PAYMENT_METHOD_OPTION.DIRECT_DEBIT
  };

  const radioInputs = [
    baseRadio,
    !isLegacyPaymentEnabled && activePet?.Underwriter === HOLLARD_UNDERWRITER && chequeRadio
  ].filter(Boolean);

  return (
    <Modal
      size="small"
      open
      onClose={handleClose}
      header={<h2 className="modal-title">Update my claim reimbursement details</h2>}
      footer={
        !isLegacyPaymentEnabled || activePet.Underwriter === PETSURE_UNDERWRITER || isPartnerUsingLegacyPayment ? (
          <div className="footerModalContainer">
            <button
              type="button"
              className="button primary outline"
              onClick={handleClose}
            >
              <span className="labelText">Cancel</span>
            </button>

            <button
              type="button"
              className="button primary"
              onClick={handleUpdateDirectDebit}
            >
              <span className="labelText">Update</span>
            </button>
          </div>
        ) : null
      }
    >
      {renderRadioButtons && (
        <StyledRadios
          name="reimbursementOption"
          onChange={handlePaymentMethodChange}
          groupName="reimbursementOption"
          groupLabel="Reimbursement option"
          radioInputs={radioInputs}
          value={paymentMethod}
          fieldFormContext={fieldFormContext}
        />
      )}

      {(activePet.Underwriter === PETSURE_UNDERWRITER || isPartnerUsingLegacyPayment) && (
        <div className="petSureForm verticalStacked reimbursementPopup">
          <DirectDebitPayment formContext={fieldFormContext} />
        </div>
      )}

      {policyDetails &&
        activePet.Underwriter === HOLLARD_UNDERWRITER &&
        !isLegacyPaymentEnabled &&
        !isPartnerUsingLegacyPayment &&
        (paymentMethod !== PAYMENT_METHOD_OPTION.DIRECT_DEBIT ? (
          <div className="directDebitContainer">
            <LegacyDirectDebitFrame
              ref={ddRef}
              publishableApiKey={
                process.env.REACT_APP_ENV !== 'prod'
                  ? process.env.WESTPAC_PUBLISHABLE_API_KEY_DEV
                  : process.env.WESTPAC_PUBLISHABLE_API_KEY_PROD
              }
              supplierBusinessCode={supplierBusinessCode}
              westpacScriptUrl={
                process.env.REACT_APP_ENV !== 'prod'
                  ? process.env.WESTPAC_SCRIPT_URL_DEV
                  : process.env.WESTPAC_SCRIPT_URL_PROD
              }
              onPaymentSuccess={handlePaymentSuccess}
            />
          </div>
        ) : (
          <div className="payByCheque">
            <span>
              We will send your reimbursement cheque to the address listed in your{' '}
              <a
                href="/my-profile/account-details"
                rel="noreferrer"
              >
                account details
              </a>
              . Alternatively, select the bank deposit method to get to your reimbursement payment faster.
            </span>
          </div>
        ))}
      {showSelectAllCheckbox && petDetails.length > 1 && (
        <fieldset className="applyToAllCheckboxPanel">
          <InputCheckbox
            fieldFormContext={fieldFormContext}
            name="applyToAll"
            label="Apply to all my pets"
            customCssClass="applyToAllCheckbox"
          />
        </fieldset>
      )}
      {showFailedUpdateError && (
        <div className="error-message-container">
          <span className="error-message">
            Sorry, we couldn&apos;t update your details at this time. Please try again in a few minutes or
            <a
              href="/contact-us"
              rel="noreferrer"
            >
              &nbsp;contact us&nbsp;
            </a>
            for assistance.
          </span>
        </div>
      )}
    </Modal>
  );
};

ReimbursementPopup.propTypes = {
  activePet: FullPetDetailsPropTypes,
  onCancel: PropTypes.func,
  setShowSuccessMessage: PropTypes.func
};
